import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  ExternalLink,
  Loader2,
  Save,
} from "lucide-react";
import { getDocData, setDocData } from "../../Firebase/Firebase.utils";
import { FormSection, TextArea, TextField } from "../components/FormShell";
import { getServiceByKey } from "../utils/services";
import { publicPageUrl } from "../utils/publicUrl";
import { revalidatePublic } from "../utils/revalidatePublic";

const SETTINGS_COLLECTION = "pageSettings";
const META_TITLE_MAX = 60;
const META_DESC_MAX = 160;

const EMPTY = { heading: "", metaTitle: "", metaDescription: "" };

export default function ServiceEdit() {
  const { serviceKey } = useParams();
  const navigate = useNavigate();
  const service = getServiceByKey(serviceKey);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [initial, setInitial] = useState(EMPTY);

  useEffect(() => {
    if (!service) {
      navigate("/cms/services", { replace: true });
      return;
    }
    let active = true;
    (async () => {
      const data = await getDocData(SETTINGS_COLLECTION, service.key);
      if (!active) return;
      const next = {
        heading: data?.heading ?? service.defaultHeading ?? "",
        metaTitle: data?.metaTitle ?? "",
        metaDescription: data?.metaDescription ?? "",
      };
      setForm(next);
      setInitial(next);
      setIsLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [service, navigate]);

  if (!service) return null;

  const isDirty = Object.keys(EMPTY).some((k) => form[k] !== initial[k]);
  const update = (field) => (e) =>
    setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Read the existing doc so we don't clobber any fields we don't
      // surface yet (e.g., subheading, heroImage might be added later).
      const existing =
        (await getDocData(SETTINGS_COLLECTION, service.key)) || {};
      await setDocData(SETTINGS_COLLECTION, service.key, {
        ...existing,
        heading: form.heading,
        metaTitle: form.metaTitle,
        metaDescription: form.metaDescription,
        updatedAt: new Date().toISOString(),
      });
      revalidatePublic([service.path]);
      setInitial(form);
      toast.success("Service page settings saved.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save.");
    } finally {
      setIsSaving(false);
    }
  };

  const saveDisabled = !isDirty || isSaving;

  return (
    <div>
      <div className="mb-4">
        <Link
          to="/cms/services"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-leechiuBlue"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Services
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="cms-eyebrow mb-1.5">Editing service page</div>
          <h1>{service.label}</h1>
          <a
            href={publicPageUrl(service.path)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-leechiuBlue hover:underline mt-2"
          >
            View public page
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FormSection
            title="Page heading"
            description="The large title displayed at the top of this services page."
          >
            <TextField
              label="Heading"
              value={form.heading}
              onChange={update("heading")}
              placeholder={service.defaultHeading}
              hint="Leave empty to use the page's default heading."
            />
          </FormSection>

          <FormSection
            title="SEO metadata"
            description="Used by search engines and social-media previews."
          >
            <div>
              <TextField
                label="Page title"
                value={form.metaTitle}
                onChange={update("metaTitle")}
                placeholder="Shown in browser tabs and search results"
                maxLength={META_TITLE_MAX + 20}
              />
              <CounterHint
                length={form.metaTitle.length}
                max={META_TITLE_MAX}
                recommendation="50–60 characters"
              />
            </div>
            <div>
              <TextArea
                label="Meta description"
                value={form.metaDescription}
                onChange={update("metaDescription")}
                rows={3}
                placeholder="Brief summary for search results and social shares"
                maxLength={META_DESC_MAX + 40}
              />
              <CounterHint
                length={form.metaDescription.length}
                max={META_DESC_MAX}
                recommendation="150–160 characters"
              />
            </div>
          </FormSection>
        </div>
      )}

      <div className="sticky bottom-0 mt-8 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-white/90 backdrop-blur border-t border-gray-200 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setForm(initial)}
          disabled={!isDirty || isSaving}
          className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm disabled:opacity-50"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saveDisabled}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-leechiuOrange text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {isSaving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}

function CounterHint({ length, max, recommendation }) {
  const over = length > max;
  return (
    <div className="mt-1 flex items-center justify-between text-[11px]">
      <span className="text-gray-400">Recommended: {recommendation}</span>
      <span className={over ? "text-red-500 font-medium" : "text-gray-400"}>
        {length}/{max}
      </span>
    </div>
  );
}
